import React from "react";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <main className="privacy-page">
      <article className="privacy-container">
        <h1 className="privacy-heading">Privacy Policy</h1>
        <p className="privacy-updated">Bonobo Hope Initiative</p>

        <p className="privacy-lead">
          Bonobo Hope Initiative (“we,” “our,” or “us”) values your privacy and
          is committed to protecting your personal information. This Privacy
          Policy outlines how we collect, use, share, and safeguard your
          information when you engage with us through our website, make
          donations, purchase merchandise, or otherwise interact with our
          mission.
        </p>

        <p className="privacy-note">
          Where required, we ask for your consent before using non-essential
          analytics cookies.
        </p>

        <Section title="1. Information We Collect">
          <Subsection
            label="A. Information You Provide Directly"
            items={[
              "Full name",
              "Email address",
              "Phone number",
              "Mailing/shipping address",
              "Billing details (processed via Stripe)",
            ]}
          />
          <p className="privacy-paragraph-inner">
            We do <strong className="privacy-strong">not</strong> store your
            full payment card information; all financial transactions are
            securely handled by{" "}
            <strong className="privacy-strong">Stripe</strong>.
          </p>

          <Subsection
            label="B. Automatically Collected Data"
            items={[
              "IP address",
              "Device and browser type",
              "Pages visited and time spent on our site",
              "Referral source and general location data",
            ]}
          />
          <p className="privacy-paragraph-inner">
            With your consent, we use{" "}
            <strong className="privacy-strong">Google Analytics</strong> to
            analyze site usage and improve performance. Learn how Google
            collects and processes data{" "}
            <a
              href="https://policies.google.com/technologies/partner-sites"
              className="privacy-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </p>

          <Subsection
            label="C. Third-Party Sources"
            items={[
              "Printify (product printing, fulfillment, shipping)",
              "Stripe (payment processing)",
            ]}
          />
        </Section>

        <Section
          title="2. How We Use Your Information"
          items={[
            "Process donations and transactions",
            "Deliver products and merchandise",
            "Send donation receipts and confirmations",
            "Communicate about programs and events",
            "Improve our website and services",
            "Meet legal obligations",
            "Prevent fraudulent activity",
          ]}
        />

        <Section title="3. Legal Basis for Processing">
          <p className="privacy-paragraph-inner">
            For users in the EU/EEA or UK, we process personal data based on:
          </p>
          <List
            items={[
              "Consent",
              "Contractual necessity",
              "Legal obligations",
              "Legitimate interests",
            ]}
          />
        </Section>

        <Section title="4. Sharing Your Information">
          <p className="privacy-paragraph-inner">
            We do <strong className="privacy-strong">not</strong> sell or rent
            your personal information. We may share it with:
          </p>
          <ul className="privacy-list">
            <li>
              <strong className="privacy-strong">Stripe</strong>:{" "}
              <a
                href="https://stripe.com/privacy"
                className="privacy-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Stripe Privacy Policy
              </a>
            </li>
            <li>
              <strong className="privacy-strong">Printify</strong>:{" "}
              <a
                href="https://printify.com/privacy-policy/"
                className="privacy-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Printify Privacy Policy
              </a>
            </li>
            <li>
              <strong className="privacy-strong">Google Analytics</strong>:{" "}
              <a
                href="https://policies.google.com/privacy"
                className="privacy-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
            </li>
            <li>Email tools and infrastructure partners</li>
          </ul>
        </Section>

        <Section title="5. Cookies and Tracking Technologies">
          <p className="privacy-paragraph-inner">
            We use essential cookies and local storage for site functionality,
            cart behavior, login sessions, payment security, and remembering
            your privacy preference. We use Google Analytics only after you
            accept analytics cookies. You can also adjust cookie settings via
            your browser.
          </p>
        </Section>

        <Section title="6. Data Retention">
          <p className="privacy-paragraph-inner">
            We retain your data only as long as necessary to fulfill our
            purposes and comply with legal obligations. We securely delete or
            anonymize data when no longer needed.
          </p>
        </Section>

        <Section title="7. Data Security">
          <p className="privacy-paragraph-inner">
            We use SSL encryption, access controls, and secure infrastructure to
            protect your information. However, no system is 100% secure.
          </p>
        </Section>

        <Section title="8. Your Rights">
          <p className="privacy-paragraph-inner">
            Depending on your jurisdiction, you may have the right to access,
            correct, delete, restrict, or export your data. You may also
            unsubscribe from communications at any time.
          </p>
        </Section>

        <Section title="9. Third-Party Links">
          <p className="privacy-paragraph-inner">
            We may link to external sites. We are not responsible for their
            privacy practices. Please review their policies.
          </p>
        </Section>

        <Section title="10. Children’s Privacy">
          <p className="privacy-paragraph-inner">
            Our site is not intended for children under 13. We do not knowingly
            collect data from children.
          </p>
        </Section>

        <Section title="11. International Visitors">
          <p className="privacy-paragraph-inner">
            If you're outside the U.S., your data may be transferred to and
            stored in the U.S. We implement safeguards as required by applicable
            laws.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p className="privacy-paragraph-inner">
            We may update this policy periodically. The latest version will
            always be available on our website.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <div className="privacy-contact">
            <p className="privacy-contact-email">Bonobo Hope Initiative</p>
            <p>
              Email:{" "}
              <a href="mailto:info@bonobohope.org" className="privacy-link">
                info@bonobohope.org
              </a>
            </p>
          </div>
        </Section>
      </article>
    </main>
  );
};

const Section = ({ title, children, items }) => (
  <section className="privacy-section">
    <h2 className="privacy-section-heading">{title}</h2>
    {children}
    {items && <List items={items} />}
  </section>
);

const Subsection = ({ label, items }) => (
  <>
    <h3 className="privacy-subsection-label">{label}</h3>
    <List items={items} />
  </>
);

const List = ({ items }) => (
  <ul className="privacy-list">
    {items.map((item, idx) => (
      <li key={idx}>{item}</li>
    ))}
  </ul>
);

export default PrivacyPolicy;
